import { faCheckCircle, faStar } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { memo, useCallback, useEffect, useRef } from 'react'
import { VariableSizeList as List } from 'react-window'
import { ShapeBase } from '~/prix'
import PulseLoaderComponent from '~/prix/react/components/pulseLoader'
import { Expression } from '~/prix/types/expression'
import { getAlertColor, getAlertLabel, getAlertTextColor } from '../variable/spaceTime.data'
import { Layer } from '../variable/spaceTime.types'
import { VariableWithConclusion } from '../variable/variables.typing'
import { HeadSection, InfoSection } from './styles/variablesView'
import ExpressionsContainer from './viewSelectModalVariable.expressionContainer'
import categories from '../categories'
import { sizeColors } from '~/design'

interface ItemProps {
  index: number
  style: React.CSSProperties
  isItemLoaded: (index: number) => boolean
  variables: VariableWithConclusion[]
  currentBaseLayer: Layer
  handleSelectVariable: (variable: VariableWithConclusion) => void
  collapsedStates: { [key: string]: boolean }
  listRef: React.RefObject<List>
  sizes: number[]
  getCollapsedOpen: (isOpen: boolean, key: string | undefined) => void
  isApiLoading: boolean
  getFilter: (expression: Record<number, Expression> | undefined, index: string) => void
  filters: Expression[] | undefined
  selectedVariableIsFilterableBy: ShapeBase | undefined
}

const Item = memo(
  ({
    index,
    style,
    isItemLoaded,
    variables,
    currentBaseLayer,
    handleSelectVariable,
    collapsedStates,
    listRef,
    sizes,
    getCollapsedOpen,
    isApiLoading,
    getFilter,
    filters,
    selectedVariableIsFilterableBy,
  }: ItemProps) => {
    const itemRef = useRef<HTMLDivElement>(null)
    const infoRef = useRef<HTMLDivElement>(null)
    const content = variables[index]
    const categoryMeta = content?.categoryKey ? categories[content.categoryKey] : undefined

    const updateSize = useCallback(() => {
      if (itemRef.current) {
        const height = itemRef.current.offsetHeight
        if (height !== sizes[index]) {
          sizes[index] = height
          if (listRef.current) {
            listRef.current.resetAfterIndex(index)
          }
        }
      }
    }, [index, sizes, listRef])

    useEffect(() => {
      updateSize()
    }, [updateSize])

    if (!isItemLoaded(index) || (isApiLoading && index === variables.length - 1)) {
      return (
        <div style={style}>
          <PulseLoaderComponent />
        </div>
      )
    }

    const presetKeyParts = content?.presetKey?.split('.') || []
    const sizeCategory = presetKeyParts.find(part => ['MEI', 'ME', 'EPP'].includes(part))

    function formatLabel(label: string) {
      const parts = label.split(' - ')
      const map: Record<string, string> = {
        Segmento: '',
        Porte: '',
        Maturidade: '',
        Curso: '',
        'Divisão CNAE': '',
        Produto: '',
      }

      parts.forEach(part => {
        const [key, ...rest] = part.split(':')
        const trimmedKey = key.trim()
        if (map.hasOwnProperty(trimmedKey)) {
          map[trimmedKey] = `${trimmedKey}: ${rest.join(':').trim()}`
        }
      })

      const formatted = Object.values(map).filter(Boolean)
      return formatted.length > 0 ? formatted : [label]
    }

    const labelSpan = content.label ? formatLabel(content.label) : []

    const isGeneralLabelOnly =
      labelSpan.length === 1 && labelSpan[0]?.trim().toLowerCase() === 'geral'

    const renderedLabel = isGeneralLabelOnly
      ? content.title
      : labelSpan.length > 0
        ? labelSpan[0]
        : content.title
    const renderedLabelLines = isGeneralLabelOnly
      ? []
      : labelSpan.length > 0
        ? [content.title, ...labelSpan.slice(1)]
        : labelSpan

    const isDisabled = false

    return (
      <div style={style} title={isDisabled ? 'Sem atendimentos cadastrados' : ''}>
        <article
          key={index}
          className={`variable ${isDisabled ? 'disabled' : ''} ${
            currentBaseLayer.variable.key === content?.key &&
            currentBaseLayer.variable.presetKey === content?.presetKey
              ? 'is-selected'
              : ''
          }`}
          ref={itemRef}
          style={{
            pointerEvents: isDisabled ? 'none' : 'auto',
            opacity: isDisabled ? 0.5 : 1,
            cursor: isDisabled ? 'not-allowed' : 'pointer',
          }}
          onClick={() => {
            getCollapsedOpen(
              !collapsedStates[`${content.title}-${content.label}` as string],
              `${content.title}-${content.label}`,
            )
            handleSelectVariable(content)
          }}
          role='button'
          tabIndex={0}
        >
          <HeadSection>
            <div className='left'>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: '6px',
                }}
              >
                <div style={{ alignSelf: 'center' }}>
                  {currentBaseLayer.variable.key === content?.key &&
                    currentBaseLayer.variable.presetKey === content?.presetKey && (
                      <div>
                        <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#0C2340' }} />
                      </div>
                    )}
                  {/*TODO DESTAQUE DE USUÁRIO Essa condição ativa as estrelinhas nos items de interesse do usuário.*/}
                  {/* {content.userHighlightedVariable && (
                    <div>
                      <FontAwesomeIcon icon={faStar} style={{ color: '#0C2340' }} />
                    </div>
                  )} */}
                </div>

                <div>
                  <div className='title'>{renderedLabel}</div>

                  {renderedLabelLines.map((line, i) => (
                    <div key={i} className='label-line'>
                      {line}
                    </div>
                  ))}

                  {sizeCategory && (
                    <div style={{ width: '50px', padding: '4px 0px 0px 0px' }}>
                      <small
                        className='alert'
                        style={{
                          backgroundColor: sizeColors[sizeCategory],
                          color: '#000',
                          padding: '2px 4px 2px 4px',
                          borderRadius: '4px',
                          fontWeight: 'bold',
                        }}
                      >
                        {sizeCategory}
                      </small>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className='right'>
              {content.conclusion?.alert && (
                <small
                  className='alert'
                  style={{
                    backgroundColor: getAlertColor(content.conclusion.alert),
                    color: getAlertTextColor(content.conclusion.alert),
                  }}
                >
                  {getAlertLabel(content.conclusion.alert)}
                </small>
              )}
            </div>
          </HeadSection>
          <InfoSection ref={infoRef}>
            {collapsedStates[`${content.title}-${content.label}` as string] && (
              <div onClick={e => e.stopPropagation()}>
                {selectedVariableIsFilterableBy && (
                  <ExpressionsContainer
                    getFilter={getFilter}
                    filtersApplied={filters}
                    isLoading={isApiLoading}
                    filtersAvailable={content?.filterableBy}
                    selectedVariableIsFilterableBy={selectedVariableIsFilterableBy}
                    resizeHeight={updateSize}
                  />
                )}
              </div>
            )}
          </InfoSection>
        </article>
      </div>
    )
  },
)

export default Item
